<template>
  <div>
    <ValidationObserver
      ref="observer"
      v-slot="{ valid, dirty }"
    >
      <v-row
        align="center"
        justify="center"
      >
        <v-col>
          <VTextField
            v-model="name[selectedLang]"
            :label="$t('productContainers.form.name')"
            :rules="{ required: teamMainLang === selectedLang, min: 1, max: 200 }"
            data-cy="name-text"
            outlined
            @input="updateData"
          />
        </v-col>
      </v-row>
      <v-row
        align="center"
        justify="center"
      >
        <v-col>
          <VSelect
            v-model="shape"
            :label="$t('productContainers.shape')"
            :rules="{ required: teamMainLang === selectedLang, min: 1, max: 200 }"
            data-cy="name-text"
            outlined
            :items="shapesFormList"
            @input="updateData"
          />
        </v-col>
        <v-col>
          <VSelect
            v-model="unit"
            :label="$t('productContainers.unit')"
            :rules="{ required: teamMainLang === selectedLang, min: 1, max: 200 }"
            data-cy="name-text"
            outlined
            :items="getUnitByShape()"
            @input="updateData"
          />
        </v-col>
      </v-row>

      <div
        v-if="shape === 'cylinder'"
      >
        <v-row
          align="left"
          justify="left"
        >
          <v-col>
            <v-radio-group
              v-model="cylinder.type"
              row
            >
              <v-radio
                v-for="itm in baseSizesList"
                :key="itm"
                :label="itm.name"
                :value="itm.id"
              >
                <template v-slot:label>
                  <div style="font-weight: 500;">
                    {{ itm.name }}
                  </div>
                </template>
              </v-radio>
            </v-radio-group>
          </v-col>
        </v-row>

        <v-row
          v-if="shape === 'cylinder' && cylinder.type"
          align="center"
          justify="center"
        >
          <v-col>
            <VTextField
              v-model.number="cylinder.radiusOrDiameterValue"
              :label="cylinder.type === 'radius' ? $t('productContainers.radiusValue') : $t('productContainers.diameterValue')"
              :rules="{ required: true }"
              data-cy="name-text"
              outlined
              :suffix="unit"
              @input="updateData"
            />
          </v-col>
          <v-col>
            <VTextField
              v-model.number="cylinder.heightValue"
              :label="$t('productContainers.heightValue')"
              :rules="{ required: true }"
              data-cy="name-text"
              outlined
              :suffix="unit"
              @input="updateData"
            />
          </v-col>
        </v-row>
      </div>


      <div
        v-if="shape === 'rectangular' && unit"
      >
        <v-row
          align="center"
          justify="center"
        >
          <v-col>
            <VTextField
              v-model.number="rectangular.lengthValue"
              :label="$t('productContainers.lengthValue')"
              :rules="{ required: true }"
              outlined
              :suffix="unit"
              @input="updateData"
            />
          </v-col>
          <v-col>
            <VTextField
              v-model.number="rectangular.widthValue"
              :label="$t('productContainers.widthValue')"
              :rules="{ required: true }"
              outlined
              :suffix="unit"
              @input="updateData"
            />
          </v-col>
          <v-col>
            <VTextField
              v-model.number="rectangular.heightValue"
              :label="$t('productContainers.heightValue')"
              :rules="{ required: true }"
              outlined
              :suffix="unit"
              @input="updateData"
            />
          </v-col>
        </v-row>
      </div>
      <SlotScopeWatcher
        :value="dirty"
        @change="$emit('dirtyChanged', dirty)"
      />
      <SlotScopeWatcher
        :value="valid"
        @change="$emit('validChanged', valid)"
      />
    </ValidationObserver>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import { SHAPES_TYPES, CYLINDER_UNIT, RECTANGULAR_UNIT, BASE_SIZES } from '@/aquassist-shared/constants/productContainers';
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect.vue';
import SlotScopeWatcher from '@/components/shared/widgets/forms/validationHelper/SlotScopeWatcher';
import { ValidationObserver } from 'vee-validate';

export default {
    components: {
        VTextField,
        VSelect,
        SlotScopeWatcher,
        ValidationObserver,
    },
    props: {
        productContainer: {
            type: Object,
            default: () => ({}),
        },
        isEdition: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            name: {},
            shape: '',
            unit: '',
            cylinder: {
                type: '', // Could be only diameter or Radius
                radiusOrDiameterValue: 0,
                heightValue: 0,
            },
            rectangular: {
                lengthValue: 0,
                widthValue: 0,
                heightValue: 0,
            },
            isLoading: true,
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'teamMainLang',
            'selectedLang',
        ]),
        shapesFormList() {
            const shapesList = SHAPES_TYPES.map(value => ({ id: value, name: this.$t(`productContainers.form.shapes.${value}`) }));
            return shapesList;
        },
        baseSizesList() {
            const baseSizesList = BASE_SIZES.map(value => ({ id: value, name: this.$t(`productContainers.${value}`) }));
            return baseSizesList;
        },
    },
    watch: {
        data: {
            async handler() {
                if (this.isLoading) return;
                const payload = {
                    name: this.name,
                    shape: this.shape,
                    unit: this.unit,
                    cylinder: this.cylinder,
                    rectangular: this.rectangular,
                };
                this.$emit('sendData', payload);
            },
            deep: true,
        },
        shape: {
            handler() {
                if (this.isLoading) return;

                this.cylinder = {
                    type: '',
                    radiusOrDiameterValue: 0,
                    heightValue: 0,
                };
                this.rectangular = {
                    lengthValue: 0,
                    widthValue: 0,
                    heightValue: 0,
                };
                this.unit = '';
            },
        },
    },
    async created() {
        if (this.isEdition) {
            this.name = { ...this.productContainer.name };
            this.shape = this.productContainer.shape;
            this.unit = this.productContainer.unit;
            this.cylinder = { ...this.productContainer.cylinder };
            this.rectangular = { ...this.productContainer.rectangular };
        }
    },
    methods: {
        updateData() {
            // J'attends que l'user puisse accéder au formulaire pour débloquer le watch sur shape
            this.isLoading = false;

            const payload = {
                name: this.name,
                shape: this.shape,
                unit: this.unit,
                cylinder: this.cylinder,
                rectangular: this.rectangular,
            };

            this.$emit('sendData', payload);
        },
        getUnitByShape() {
            switch (this.shape) {
                case 'cylinder':
                    return CYLINDER_UNIT.map(value => ({ id: value, name: this.$t(`productContainers.units.${value}`) }));
                case 'rectangular':
                    return RECTANGULAR_UNIT.map(value => ({ id: value, name: this.$t(`productContainers.units.${value}`) }));
                default:
                    return [];
            }
        },
    },
};
</script>
